import WizardSidebar, { type WizardSidebarRow } from '../wizard/WizardSidebar'
import type { Ct600Status, StoredCompany } from '../../types'
import type { CT600SetupForm } from '../../types/ct600Filing'

const statusBadgeStyles: Record<Ct600Status, string> = {
  draft: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-blue-100 text-blue-700',
  submitted: 'bg-indigo-100 text-indigo-700',
  processing: 'bg-amber-100 text-amber-700',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  partial: 'bg-orange-100 text-orange-700',
}

function formatDate(dateString: string): string {
  if (!dateString) return '—'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return '—'
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return '—'
  }
}

function calculateDeadline(periodEnd: string): string {
  if (!periodEnd) return '—'
  try {
    const end = new Date(periodEnd)
    if (isNaN(end.getTime())) return '—'
    const deadline = new Date(end)
    deadline.setFullYear(deadline.getFullYear() + 1)
    return deadline.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return '—'
  }
}

interface Props {
  company: StoredCompany | null
  setup: CT600SetupForm
  formStatus: Ct600Status | null
  /** Width utility class forwarded to the underlying WizardSidebar. */
  width?: string
}

export default function CT600WizardSidebar({
  company,
  setup,
  formStatus,
  width = 'w-80',
}: Props) {
  const companyName =
    company?.companyName || company?.name || setup.companyName || '—'
  const companyNumber = company?.companyNumber || setup.companyNumber || '—'
  const utr = setup.utr || company?.hmrcCredentials?.companyUtr || '—'

  const rows: WizardSidebarRow[] = [
    { label: 'Company', value: companyName },
    { label: 'Company Number', value: companyNumber },
    { label: 'UTR', value: utr },
  ]
  if (setup.accountingPeriodStart) {
    rows.push({
      label: 'Accounting Period',
      value: `${formatDate(setup.accountingPeriodStart)} — ${formatDate(setup.accountingPeriodEnd)}`,
    })
  }
  if (setup.accountingPeriodEnd) {
    rows.push({
      label: 'Filing Deadline',
      value: calculateDeadline(setup.accountingPeriodEnd),
    })
  }

  return (
    <WizardSidebar
      rows={rows}
      width={width}
      status={
        formStatus
          ? {
              label: formStatus.charAt(0).toUpperCase() + formStatus.slice(1),
              className: statusBadgeStyles[formStatus],
            }
          : null
      }
    />
  )
}
